/**
 * StarPicker — accessible 5-star rating control.
 *
 * Extracted from VerificationDetailPage.jsx (where it lived inline)
 * so the new SessionReviewPage can reuse it without copy-paste.
 * Visual + interaction behaviour intentionally identical so async-review
 * and live-session rating feel consistent — both are the "leave feedback
 * for a credentialed human" UX shape.
 *
 * Props:
 *   value     — current rating (1-5) or null/undefined for unrated
 *   onChange  — called with the chosen rating when the user clicks
 *   disabled  — disables interaction (used during submitting state)
 *
 * Accessibility:
 *   • Each star is a real <button> with aria-label "N star(s)"
 *   • Hover state previews the rating (mouse), without committing
 *   • The visible count "N / 5" appears once a rating is set
 */
import { useState } from "react";
import { Star } from "lucide-react";

export default function StarPicker({ value, onChange, disabled }) {
  const [hovered, setHovered] = useState(null);
  const display = hovered ?? value ?? 0;
  return (
    <div
      className="flex items-center gap-1"
      onMouseLeave={() => setHovered(null)}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= display;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onMouseEnter={() => setHovered(n)}
            onClick={() => onChange(n)}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            className={`p-1 transition-transform ${
              disabled ? "cursor-default" : "cursor-pointer hover:scale-110"
            }`}
          >
            <Star
              className={`w-7 h-7 ${
                filled
                  ? "fill-brand-green text-brand-green"
                  : "text-slateline-300"
              }`}
            />
          </button>
        );
      })}
      {value && (
        <span className="ml-2 text-sm text-slateline-500">
          {value} / 5
        </span>
      )}
    </div>
  );
}
